import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FiBell, FiCalendar, FiChevronDown, FiSearch } from "react-icons/fi";
import WalkInAlertsPill from "../../components/WalkInAlertsPill";

interface ActiveAgents {
  active: number;
  total: number;
  display: string;
}

interface OverviewStats {
  activeAgents: ActiveAgents;
  activeOrders: number;
  completedToday: number;
  avgFulfillmentMinutes: number;
  avgFulfillmentDisplay: string;
}

interface AgentSession {
  agentId: string;
  name: string;
  sessionStart: string | null;
  duration: string;
  orders: number;
  revenue: number;
  currentStatus: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const FILTER_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "month", label: "This Month" },
  { value: "yearly", label: "This Year" },
] as const;

const STATUS_TABS = [
  { value: "all", label: "All Agents" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
] as const;

type FilterType = typeof FILTER_OPTIONS[number]["value"];
type StatusType = typeof STATUS_TABS[number]["value"];

export default function AgentManagement() {
  const [filter, setFilter] = useState<FilterType>("today");
  const [status, setStatus] = useState<StatusType>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [agents, setAgents] = useState<AgentSession[]>([]);
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [pagination, setPagination] = useState<PaginationData | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getToken = () => {
    const auth = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    return jsonData?.token as string | undefined;
  };

  const authHeaders = (token?: string) =>
    token ? { Authorization: `Bearer ${token}` } : undefined;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 2,
    }).format(amount);

  const fetchOverviewStats = useCallback(async () => {
    setIsStatsLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/agents/overview?filter=${filter}`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setStats(res.data.data.stats);
      }
    } catch (err: any) {
      console.error("Failed to fetch agent overview stats:", err);
    } finally {
      setIsStatsLoading(false);
    }
  }, [filter]);

  const fetchAgentsList = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = getToken();
      const queryParams = new URLSearchParams();
      queryParams.append("filter", filter);
      queryParams.append("status", status);
      queryParams.append("page", String(currentPage));
      queryParams.append("limit", "10");
      if (debouncedSearchQuery.trim()) {
        queryParams.append("q", debouncedSearchQuery.trim());
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/agents?${queryParams.toString()}`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setAgents(res.data.data.data ?? []);
        setPagination(res.data.data.pagination ?? null);
      } else {
        setError("Unexpected response from server");
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || "Failed to fetch agents";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }, [filter, status, debouncedSearchQuery, currentPage]);

  // Debounce search query input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1); // Reset to page 1 on new search query
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    fetchOverviewStats();
  }, [fetchOverviewStats]);

  useEffect(() => {
    fetchAgentsList();
  }, [fetchAgentsList]);

  // Reset page when filter or status tabs change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, status]);

  const handleExportCSV = () => {
    if (!agents || agents.length === 0) {
      toast.error("No agent data available to export");
      return;
    }

    const headers = ["Agent ID", "Name", "Session Start", "Duration", "Orders Filled", "Revenue (NGN)", "Current Status"];
    const rows = agents.map((agent) => [
      agent.agentId,
      agent.name,
      agent.sessionStart || "N/A",
      agent.duration,
      agent.orders,
      agent.revenue,
      agent.currentStatus,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `agents_management_report_${filter}_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Agent summary report exported successfully");
  };

  const getFilterLabel = () => {
    return FILTER_OPTIONS.find((f) => f.value === filter)?.label || "Today";
  };

  return (
    <div className="w-full min-h-screen p-4 xl:p-6 flex flex-col gap-6 font-sans">
      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-gray-950 tracking-tight">AGENTS</h1>
          <p className="text-gray-400 text-sm mt-0.5">Live fulfilment queue — all statuses</p>
        </div>

        <div className="flex items-center gap-3 self-stretch md:self-auto flex-wrap">
          <WalkInAlertsPill />

          {/* Date range filter dropdown */}
          <div className="relative">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as FilterType)}
              className="appearance-none bg-white border border-gray-100 rounded-lg pl-10 pr-8 py-2 text-sm font-medium text-gray-700 focus:outline-none focus:border-[#008060] cursor-pointer shadow-sm"
            >
              {FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <FiCalendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#008060] pointer-events-none" />
            <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          </div>

          <button
            onClick={handleExportCSV}
            className="flex items-center justify-between gap-3 px-4 py-2 bg-[#008060] text-white rounded-lg text-sm font-semibold hover:bg-[#006b50] transition-colors shadow-sm min-w-[150px]"
          >
            <span>Export Summary</span>
            <FiChevronDown className="text-white" />
          </button>
        </div>
      </div>

      {/* Row of Metrics (4 cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Metric 1: Active Agents */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Active Agents</p>
          <div className="flex items-baseline gap-1.5 mt-2">
            {isStatsLoading ? (
              <div className="h-7 w-20 bg-gray-100 rounded animate-pulse my-1" />
            ) : (
              <h3 className="text-[26px] font-black text-gray-900 leading-none">
                {stats?.activeAgents?.display ?? "0/0"}
              </h3>
            )}
            <span className="text-[#008060] text-sm font-bold select-none">↑</span>
          </div>
          <p className="text-gray-400 text-[11px] font-medium mt-1">{getFilterLabel()}</p>
        </div>

        {/* Metric 2: Active Orders */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Active Orders</p>
          <div className="flex items-baseline gap-1.5 mt-2">
            {isStatsLoading ? (
              <div className="h-7 w-12 bg-gray-100 rounded animate-pulse my-1" />
            ) : (
              <h3 className="text-[26px] font-black text-gray-900 leading-none">
                {stats?.activeOrders ?? 0}
              </h3>
            )}
            <span className="text-[#008060] text-sm font-bold select-none">↑</span>
          </div>
          <p className="text-gray-400 text-[11px] font-medium mt-1">{getFilterLabel()}</p>
        </div>

        {/* Metric 3: Completed */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Completed Today</p>
          {isStatsLoading ? (
            <div className="h-7 w-16 bg-gray-100 rounded animate-pulse my-2" />
          ) : (
            <h3 className="text-[26px] font-black text-gray-900 mt-2 leading-none">
              {stats?.completedToday ?? 0}
            </h3>
          )}
          <p className="text-gray-400 text-[11px] font-medium mt-1">{getFilterLabel()}</p>
        </div>

        {/* Metric 4: Avg Fulfillment */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Avg Fulfillment</p>
          {isStatsLoading ? (
            <div className="h-7 w-20 bg-gray-100 rounded animate-pulse my-2" />
          ) : (
            <h3 className="text-[26px] font-black text-gray-900 mt-2 leading-none">
              {stats?.avgFulfillmentDisplay ?? "0mins"}
            </h3>
          )}
          <p className="text-gray-400 text-[11px] font-medium mt-1">{getFilterLabel()}</p>
        </div>
      </div>

      {/* Search & Status Tabs Bar */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
        <div className="relative flex-1 w-full md:max-w-md">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by agent name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-white border border-gray-100 rounded-xl text-sm text-gray-800 focus:outline-none focus:border-[#008060] transition-all shadow-sm placeholder-gray-400"
          />
        </div>

        <div className="flex bg-gray-50 p-1 rounded-xl border border-gray-100 gap-0.5">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setStatus(tab.value)}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                status === tab.value
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-400 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Agent list table and contents */}
      {error ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 border border-dashed border-gray-200 rounded-2xl bg-gray-50/50">
          <p className="text-sm text-gray-500 font-medium mb-3">{error}</p>
          <button
            onClick={() => {
              fetchOverviewStats();
              fetchAgentsList();
            }}
            className="px-4 py-2 bg-[#008060] text-white rounded-lg text-xs font-bold hover:bg-[#006b50] transition-colors shadow-sm"
          >
            Retry Request
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-50 p-5">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-[#EAF5EF] text-[#008060] text-xs font-bold rounded-lg">
                  <th className="p-3.5 pl-4 rounded-l-lg font-bold tracking-wide">Name</th>
                  <th className="p-3.5 font-bold tracking-wide">Session Start</th>
                  <th className="p-3.5 font-bold tracking-wide">Duration</th>
                  <th className="p-3.5 font-bold tracking-wide">Orders</th>
                  <th className="p-3.5 font-bold tracking-wide">Revenue</th>
                  <th className="p-3.5 pr-4 rounded-r-lg font-bold tracking-wide">Current Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50/50 text-gray-700 font-medium">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={`skeleton-${idx}`} className="animate-pulse">
                      <td className="p-3.5 pl-4">
                        <div className="h-4 w-32 bg-gray-100 rounded" />
                      </td>
                      <td className="p-3.5">
                        <div className="h-4 w-20 bg-gray-100 rounded" />
                      </td>
                      <td className="p-3.5">
                        <div className="h-4 w-16 bg-gray-100 rounded" />
                      </td>
                      <td className="p-3.5">
                        <div className="h-4 w-12 bg-gray-100 rounded" />
                      </td>
                      <td className="p-3.5">
                        <div className="h-4 w-24 bg-gray-100 rounded" />
                      </td>
                      <td className="p-3.5 pr-4">
                        <div className="h-4 w-20 bg-gray-100 rounded" />
                      </td>
                    </tr>
                  ))
                ) : agents.length > 0 ? (
                  agents.map((agent) => {
                    const isStatusActive = agent.currentStatus?.toLowerCase() === "active";
                    return (
                      <tr key={agent.agentId} className="hover:bg-gray-50/30 transition-colors">
                        <td className="p-3.5 pl-4 text-gray-900 font-semibold">{agent.name}</td>
                        <td className="p-3.5 text-gray-500">{agent.sessionStart || "Not started"}</td>
                        <td className="p-3.5 text-gray-500">{agent.duration}</td>
                        <td className="p-3.5 text-gray-900">{agent.orders}</td>
                        <td
                          className={`p-3.5 font-bold ${
                            agent.revenue > 0 ? "text-[#008060]" : "text-gray-400"
                          }`}
                        >
                          {formatCurrency(agent.revenue)}
                        </td>
                        <td className="p-3.5 pr-4">
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`w-2 h-2 rounded-full ${
                                isStatusActive ? "bg-emerald-500 animate-pulse" : "bg-gray-300"
                              }`}
                            />
                            <span
                              className={`text-xs font-bold ${
                                isStatusActive ? "text-emerald-500" : "text-gray-400"
                              }`}
                            >
                              {agent.currentStatus || "Inactive"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-gray-400">
                      No agents found matching the selected criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {!isLoading && pagination && pagination.totalPages > 1 && (
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={!pagination.hasPrevPage}
                className="text-xs font-semibold text-gray-600 disabled:text-gray-300 transition-colors"
              >
                Previous
              </button>
              <span className="text-xs text-gray-400">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={!pagination.hasNextPage}
                className="text-xs font-semibold text-[#008060] disabled:text-gray-300 transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
